import React from "react";
import { Link } from "react-router-dom";
import { Check, User, Users, Network, Building2, ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CtaBand from "@/components/CtaBand";
import Seo from "@/components/Seo";
import { SERVICES } from "@/lib/content";

const icons = { "single-hire": User, "team-pod": Users, "offshore-function": Network, "india-office": Building2 };

const Services = () => {
  return (
    <>
      <Seo
        title="Services — Single Hire to Offshore Function | Bright EOR"
        description="Four service levels for UK businesses hiring in India: single-hire EOR, managed team pods, complete offshore functions and dedicated India offices."
        path="/services"
      />
      <PageHeader
        eyebrow="Services"
        title="From one dedicated hire to a complete offshore function."
        subtitle="Four clearly defined service levels — start with a single employee and scale into a team, a full function or a dedicated India office. Single-hire EOR remains at the core of everything we do."
      />
      <section className="py-20 lg:py-28" data-testid="services-detail">
        <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12 space-y-16">
          {SERVICES.map((s) => {
            const Icon = icons[s.slug] || User;
            return (
              <div key={s.slug} className="grid gap-10 lg:grid-cols-12 lg:gap-12 items-start" data-testid={`service-detail-${s.slug}`}>
                <div className="lg:col-span-5">
                  <div className="w-14 h-14 rounded-2xl bg-aqua/15 text-navy flex items-center justify-center"><Icon className="h-6 w-6" /></div>
                  <p className="mt-6 text-xs uppercase tracking-[0.22em] text-aqua font-semibold">{s.level}</p>
                  <h2 className="font-display mt-3 text-3xl sm:text-4xl font-semibold text-navy leading-[1.08] tracking-tight">{s.headline}</h2>
                  <p className="mt-4 text-lg text-slateink leading-relaxed">{s.tagline}</p>
                  <p className="mt-3 text-[15px] text-slatemuted leading-relaxed">{s.body}</p>
                  {s.control && (
                    <div className="mt-6 rounded-2xl border border-aqua/25 bg-aqua/5 p-5">
                      <p className="text-[14px] text-navy leading-relaxed">{s.control}</p>
                    </div>
                  )}
                </div>

                <div className="lg:col-span-7">
                  <div className="rounded-3xl border border-slate-200 bg-white p-8 lg:p-10 shadow-card-soft">
                    <p className="text-xs uppercase tracking-[0.22em] text-slatemuted font-semibold">Included</p>
                    <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                      {s.includes.map((inc) => (
                        <li key={inc} className="flex items-start gap-2.5 text-[15px] text-slateink">
                          <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-aqua/15"><Check className="h-3 w-3 text-aqua" strokeWidth={3} /></span>
                          {inc}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {s.options && (
                    <div className="mt-5 grid gap-4 sm:grid-cols-2">
                      {s.options.map((o) => (
                        <div key={o.name} className="rounded-2xl border border-slate-200 bg-navy-50 p-5">
                          <p className="font-display text-[15px] font-semibold text-navy">{o.name}</p>
                          <p className="mt-2 text-[13.5px] text-slateink leading-relaxed">{o.body}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {s.slug === "team-pod" || s.slug === "offshore-function" ? (
                    <Link to="/managed-teams" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-aqua transition-colors" data-testid={`service-link-${s.slug}`}>
                      Explore Managed Teams <ArrowRight className="h-4 w-4" />
                    </Link>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <CtaBand />
    </>
  );
};

export default Services;
